import type { components } from '@/api/schema';

import { keysOf, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

export type LeadSource = Schemas['LeadIntakeInput']['source'];
export type LeadDisposition = Schemas['LeadView']['disposition'];
export type LeadDraftKind = Schemas['LeadDraftInput']['kind'];
export type SafetyAnswer = Schemas['LeadContact']['safety'][string];
export type LostSaleReason = Schemas['LostSaleReasonInput']['code'];

/** What the server's own rules sort a request into; the schema leaves it open. */
export type LeadClassification =
  'REPLACEMENT' | 'REPAIR' | 'MAINTENANCE' | 'IAQ' | 'ACCESSORY' | 'SERVICE' | 'INFO' | 'UNKNOWN';

/** The screening questions asked at intake; the schema keys the answers by free string. */
export type SafetySignal =
  'GAS_ODOR' | 'CO' | 'FIRE' | 'ELECTRICAL' | 'FLOODING' | 'UNSAFE_CONDITIONS' | 'NO_HEAT_NO_COOL';

/** The reminders the intake sweep opens on a lead; the schema carries them as plain strings. */
export type LeadTask = 'UNANSWERED_LEAD' | 'OVERDUE_DISPOSITION';

/** Where a lead's message stands; the schema leaves it open. */
export type LeadOutboundStatus =
  'NONE' | 'DRAFT' | 'SEND_APPROVED' | 'SENT' | 'DELIVERY_FAILED' | 'SUPPRESSED';

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  REFERRAL: 'Referral',
  WEB_FORM: 'Website',
  PHONE: 'Phone',
  AD: 'Advertising',
  CHAT: 'Chat',
  TECHNICIAN_GENERATED: 'Service technician',
  OTHER: 'Other',
};

export const LEAD_DISPOSITION_LABELS: Record<LeadDisposition, string> = {
  OPEN: 'Open',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  CLOSED: 'Closed',
};

export const LEAD_CLASSIFICATION_LABELS: Record<LeadClassification, string> = {
  REPLACEMENT: 'Replacement',
  REPAIR: 'Repair',
  MAINTENANCE: 'Maintenance',
  IAQ: 'Indoor air quality',
  ACCESSORY: 'Accessory',
  SERVICE: 'Service',
  INFO: 'Information request',
  UNKNOWN: 'Not classified yet',
};

export const SAFETY_SIGNAL_LABELS: Record<SafetySignal, string> = {
  GAS_ODOR: 'Gas odor',
  CO: 'Carbon monoxide concern',
  FIRE: 'Fire',
  ELECTRICAL: 'Electrical hazard',
  FLOODING: 'Flooding',
  UNSAFE_CONDITIONS: 'Unsafe conditions',
  NO_HEAT_NO_COOL: 'No heat or cooling',
};

export const SAFETY_ANSWER_LABELS: Record<SafetyAnswer, string> = {
  YES: 'Yes',
  NO: 'No',
  UNKNOWN: 'Unknown',
};

export const LEAD_DRAFT_KIND_LABELS: Record<LeadDraftKind, string> = {
  PROSPECT: 'Prospect summary',
  CLASSIFY_ROUTE: 'Classification and routing',
  CONFIRMATION: 'Customer confirmation',
  HANDOFF: 'Handoff note',
  ESCALATION: 'Safety escalation',
  SEND_CONFIRMATION: 'Confirmation to send',
};

export const LEAD_TASK_LABELS: Record<LeadTask, string> = {
  UNANSWERED_LEAD: 'First contact overdue',
  OVERDUE_DISPOSITION: 'Intake outcome overdue',
};

export const LEAD_OUTBOUND_LABELS: Record<LeadOutboundStatus, string> = {
  NONE: 'No message drafted',
  DRAFT: 'Draft waiting for approval',
  SEND_APPROVED: 'Send approved · delivery pending',
  SENT: 'Sent',
  DELIVERY_FAILED: 'Delivery failed',
  SUPPRESSED: 'Not sent — the customer asked not to be contacted',
};

export const LOST_SALE_REASON_LABELS: Record<LostSaleReason, string> = {
  PRICE: 'Price',
  TIMING: 'Timing',
  SCOPE: 'Scope or fit',
  COMPETITOR: 'Another provider',
  FINANCING: 'Financing',
  TRUST: 'Trust or confidence',
  OTHER: 'Other',
};

/** The lead sources as `{ value, label }`, for a select. */
export const LEAD_SOURCES = optionsOf(LEAD_SOURCE_LABELS);

/** The lost-sale headings as `{ value, label }`, for a select. */
export const LOST_SALE_REASONS = optionsOf(LOST_SALE_REASON_LABELS);

/** The screening questions, in the order they are asked. */
export const SAFETY_SIGNALS = keysOf(SAFETY_SIGNAL_LABELS);

/**
 * What a stored lead source is called on screen.
 *
 * A value outside the vocabulary is humanized rather than hidden: a record written before the
 * vocabulary existed is still a record somebody has to be able to read.
 *
 * @param stored the value on the record
 * @returns the label a person reads
 */
export function leadSourceLabel(stored: string | null | undefined): string {
  if (stored === null || stored === undefined || stored === '') {
    return 'Unrecorded';
  }
  return labelOf(LEAD_SOURCE_LABELS, stored);
}

export function leadDispositionLabel(disposition: string | null | undefined): string {
  return labelOf(LEAD_DISPOSITION_LABELS, disposition);
}

export function leadClassificationLabel(classification: string | null | undefined): string {
  return labelOf(LEAD_CLASSIFICATION_LABELS, classification);
}

export function safetySignalLabel(signal: string): string {
  return labelOf(SAFETY_SIGNAL_LABELS, signal);
}

export function safetyAnswerLabel(answer: string | null | undefined): string {
  return labelOf(SAFETY_ANSWER_LABELS, answer);
}

export function leadDraftKindLabel(kind: string | null | undefined): string {
  return labelOf(LEAD_DRAFT_KIND_LABELS, kind);
}

// An assistant record names the same drafts with a LEAD_ prefix: LEAD_HANDOFF is the HANDOFF kind.
export function leadActionLabel(action: string | null | undefined): string {
  return leadDraftKindLabel(action?.replace(/^LEAD_/, ''));
}

export function leadTaskLabel(task: string | null | undefined): string {
  return labelOf(LEAD_TASK_LABELS, task);
}

export function leadOutboundLabel(status: string | null | undefined): string {
  return labelOf(LEAD_OUTBOUND_LABELS, status);
}

export function lostSaleReasonLabel(code: string | null | undefined): string {
  return labelOf(LOST_SALE_REASON_LABELS, code);
}
